/**
 * Login page for unauthenticated users (moved out of App.tsx unchanged on 2026-11-03, so it renders under test without
 * App.tsx's MSAL setup at import).
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';

/**
 * Login page for unauthenticated users
 * 
 * Microsoft Best Practice: Allow users to specify a loginHint (email/UPN)
 * to pre-fill the username field and streamline the login experience.
 * Leave blank to use account picker with currently signed-in accounts.
 */
const LoginPage: React.FC = () => {
  const { login, isLoading, error, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loginHint, setLoginHint] = React.useState('');

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async () => {
    try {
      // Pass loginHint to login function (empty string uses account picker)
      await login(loginHint || undefined);
      // Navigate to dashboard after successful login
      navigate('/dashboard', { replace: true });
    } catch (err) {
      // Error is already handled by useAuth
      console.error('Login error:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-[image:var(--site-grad-stage)] font-[family-name:var(--site-font)]'>
      <div className='bg-[var(--site-navy)] rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4'>
        <div className='text-center mb-8'>
          <h1 className='text-3xl font-bold text-[var(--site-on-dark-heading)] mb-2'>Fabric Control Center</h1>
          <p className='text-[var(--site-on-dark-text)]'>Sign in with your Microsoft account to continue</p>
        </div>

        {error && (
          <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
            <p className='text-sm text-red-600'>{typeof error === 'string' ? error : (error as { message: string }).message}</p>
          </div>
        )}

        {/* Login Hint Input Field */}
        <div className='mb-6'>
          <label htmlFor='loginHint' className='block text-sm font-medium text-[var(--site-on-dark-link)] mb-2'>
            Microsoft Account (optional)
          </label>
          <input
            type='email'
            id='loginHint'
            value={loginHint}
            onChange={(e) => setLoginHint(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder='user@domain.com'
            className='w-full px-4 py-3 bg-[var(--site-navy-deep)] text-[var(--site-on-dark-heading)] placeholder:text-[var(--site-on-dark-muted)] border border-[var(--site-on-dark-line)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--site-azure)] focus:border-transparent transition-colors'
            disabled={isLoading}
            autoComplete='email'
            autoFocus
          />
          <p className='mt-2 text-xs text-[var(--site-on-dark-muted)]'>
            Enter your email to sign in directly, or leave blank to choose from signed-in accounts.
          </p>
        </div>

        <button
          onClick={handleLogin}
          disabled={isLoading}
          className='w-full flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-[image:var(--site-grad-btn)] text-[var(--site-on-dark-heading)] font-semibold hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--site-azure)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--site-navy)] transition-[filter] disabled:opacity-50 disabled:cursor-not-allowed'
        >
          {isLoading ? (
            <>
              <div className='animate-spin w-5 h-5 border-2 border-[var(--site-on-dark-heading)] border-t-transparent rounded-full' />
              Signing in...
            </>
          ) : (
            <>
              <svg className='w-5 h-5' viewBox='0 0 21 21' fill='currentColor'>
                <path d='M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z' />
              </svg>
              Sign in with Microsoft
            </>
          )}
        </button>

        <p className='mt-6 text-center text-xs text-[var(--site-on-dark-muted)]'>Protected by Microsoft Entra ID</p>
      </div>
    </div>
  );
};

export default LoginPage;
